// DataSource 的雛形實作（§3.3：React UI 不連接原系統 API）。
//
// 這個檔案就是「保留接口」的那個接口：頁面只認識 src/mocks/types.ts 的 DataSource，
// 日後要接真 API 時新增一個 HttpDataSource 實作同一個介面、在 provider.tsx 換掉
// `new MockDataSource(...)` 這一行即可，頁面與元件一行都不用改。
//
// ponytail: 一個 Map（resource → Row[]）＋ fixtures.ts 的 seeded 產生器就夠了，
// 不建 repository／unit-of-work／entity registry 階層。查詢就是在陣列上 filter/sort/slice，
// 因為雛形的資料量是 137 筆，不是 137 萬筆。升級路徑：真 API 進來時整個檔案作廢。

import { buildId, buildRows, columnsFor } from '@/mocks/fixtures';
import {
  type LegacyActionKeyword,
  resolveAllLegacyMessages,
  resolveLegacyMessageForAction,
} from '@/mocks/legacy-message-resolver';
import { STATUS_ENABLED } from '@/mocks/page-columns';
import type { DataSource, ListQuery, ListResult, Row, ScenarioState } from '@/mocks/types';

// ---------------------------------------------------------------------------
// 錯誤型別
// ---------------------------------------------------------------------------

export type MockErrorCode = 'error' | 'unauthorized' | 'validation-error' | 'not-found';

/**
 * DataSource 丟出的錯誤。帶 `code` 讓頁面決定要顯示哪一則固定文字
 * （§17：畫面文字集中在 src/messages/zh-TW.ts，不要直接把 error.message 印到畫面上）。
 */
export class MockDataError extends Error {
  readonly code: MockErrorCode;
  /** validation-error 時的欄位錯誤：欄位 key → 訊息。其他情況為空物件。 */
  readonly fields: Readonly<Record<string, string>>;
  /**
   * FC-5 R3：這頁這個分類的 legacy 驗證訊息數超過可用欄位數時，多出來的訊息
   * （多槽位顯示——不再只挑一條代表，見 `legacy-message-resolver.ts` 的
   * `resolveAllLegacyMessages()`）。其他情況為空陣列。
   */
  readonly extraMessages: readonly string[];

  constructor(
    code: MockErrorCode,
    message: string,
    fields: Readonly<Record<string, string>> = {},
    extraMessages: readonly string[] = [],
  ) {
    super(message);
    this.name = 'MockDataError';
    this.code = code;
    this.fields = fields;
    this.extraMessages = extraMessages;
  }
}

// ---------------------------------------------------------------------------
// scenario → 行為
// ---------------------------------------------------------------------------

/**
 * 每個 scenario 的資料筆數。用 Record<ScenarioState, number> 宣告，
 * 所以 types.ts 之後加了新的 scenario 而這裡漏掉，會直接編譯失敗。
 */
export const ROW_COUNTS: Readonly<Record<ScenarioState, number>> = {
  default: 12,
  populated: 24,
  empty: 0,
  loading: 12,
  error: 12,
  'validation-error': 12,
  create: 12,
  edit: 12,
  'view-only': 12,
  confirmation: 12,
  success: 12,
  unauthorized: 12,
  // filter：筆數夠多、且欄位值分散，keyword 才看得出真的有過濾掉東西。
  filter: 36,
  // pagination：刻意不是 pageSize 的整數倍，最後一頁會是不滿頁（原系統常見的邊界）。
  pagination: 137,
  export: 24,
  'write-error': 12,
};

/** 原系統 DataTable 的預設每頁筆數。 */
export const DEFAULT_PAGE_SIZE = 10;

/** loading scenario 的預設延遲。測試與 Storybook 可以用 options 改成 0。 */
export const DEFAULT_LOADING_DELAY_MS = 1200;

const MESSAGE_ERROR = '資料載入失敗，請稍後再試。';
const MESSAGE_UNAUTHORIZED = '您沒有權限存取此功能';
const MESSAGE_VALIDATION = '欄位驗證未通過，請檢查標示的欄位。';
const MESSAGE_NOT_FOUND = '找不到指定的資料。';

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// ---------------------------------------------------------------------------
// localStorage 後盾（batch 0：CRUD 異動要撐過重新整理，見 docs/plans/
// 2026-09-17-batch0-progress.md 階段 1 的裁決紀錄）
// ---------------------------------------------------------------------------

/** localStorage key 前綴。重置測試資料時只要抓這個前綴就能找到全部要清的 key。 */
const STORAGE_PREFIX = 'eportfolio-ui.mock-data';

interface PersistedResource {
  rows: Row[];
  /** 撐過重新整理，新增流水號才不會在下次新增時撞號。 */
  createdCount: number;
}

/**
 * key 含 scenario：不同 scenario 的種子筆數不同（ROW_COUNTS），彼此的 CRUD 異動
 * 不該互相污染（例如 `?scenario=pagination` 測試跑過的髒資料不該滲進 `default` 的
 * 展示資料）。scenario 只在整頁載入時決定一次（provider.tsx：站內導覽不會變 scenario），
 * 所以用它分桶不會有「使用中途鑰匙變動」的問題。
 */
function storageKey(scenario: ScenarioState, resource: string): string {
  return `${STORAGE_PREFIX}.${scenario}.${resource}`;
}

/**
 * 讀 localStorage。跟 provider.tsx 的 readStoredSession() 同一個理由整段包 try：
 * 隱私視窗、Cookie 全擋、或配額爆掉時，**光是存取 `window.localStorage` 這個 property
 * 本身就會丟例外**，不是只有 getItem() 會丟。讀不到／格式不對就當作「沒有既存資料」，
 * 呼叫端退回 fixtures 產生種子資料——純記憶體降級，不提示使用者（比照 provider.tsx
 * 對 sessionStorage 的既有先例：失敗一律當成「沒有既存資料」，畫面照常運作）。
 */
function readPersistedResource(
  scenario: ScenarioState,
  resource: string,
): PersistedResource | null {
  try {
    const raw = window.localStorage.getItem(storageKey(scenario, resource));
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !Array.isArray((parsed as { rows?: unknown }).rows)
    ) {
      return null;
    }
    const createdCount = (parsed as { createdCount?: unknown }).createdCount;
    return {
      rows: (parsed as { rows: Row[] }).rows,
      createdCount: typeof createdCount === 'number' ? createdCount : 0,
    };
  } catch {
    // 讀不到就當作沒有既存資料；呼叫端會退回 fixtures 種子資料。
    return null;
  }
}

/**
 * 寫不進去（配額爆掉、隱私視窗）不影響畫面，只是這次異動撐不過下一次重新整理——
 * 跟 provider.tsx 的 writeStoredSession() 一樣，不提示使用者。
 */
function writePersistedResource(
  scenario: ScenarioState,
  resource: string,
  data: PersistedResource,
): void {
  try {
    window.localStorage.setItem(storageKey(scenario, resource), JSON.stringify(data));
  } catch {
    // 降級：這個 resource 之後只在記憶體內生效，直到下一次寫入成功為止。
  }
}

/**
 * 「重置測試資料」機制的資料層那一半：清掉所有批次 0 的 localStorage key（不分
 * scenario——使用者重置時不會只想清目前這個 scenario 的髒資料），下一次
 * MockDataSource 初始化時就會全部退回 fixtures 種子資料。
 * UI／reload 那一半在 `provider.tsx` 的 `useResetTestData()`。
 */
export function clearAllPersistedMockData(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(`${STORAGE_PREFIX}.`)) {
        keys.push(key);
      }
    }
    for (const key of keys) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // 存取不到 localStorage：本來就沒有跨重新整理的髒資料需要清，等同重置成功。
  }
}

/**
 * 批次 52（§8-B 測試前置；裁定 B）：29 頁種子空表化後，行為測試需要「既有列」時，
 * 把指定 resource 的種子列直接寫進 persisted 區——rowsFor 讀 persisted 優先於種子生成，
 * 頁面看到的即是「既有資料」。列由 buildRows＋enrichResourceRows 產生（與空表化前的
 * 種子完全同源，行為測試斷言不變；config-role 12 列＝9 系統＋2 自訂＋1 泛用舊態）。
 * 測試專用：正式種子路徑走 rowsFor（EMPTY_TABLE_RESOURCES／SEED_ROW_COUNTS 控管）。
 */
export function seedPersistedRows(
  resource: string,
  count: number,
  scenario: ScenarioState = 'default',
): void {
  writePersistedResource(scenario, resource, {
    rows: enrichResourceRows(resource, buildRows(resource, count)),
    createdCount: 0,
  });
}

/**
 * FC-5 R3：這次 `update()` 是不是「狀態切換」（CrudListPage 的啟用/停用鈕），
 * 用來決定 gate() 該用哪個動作關鍵字挑 confirm/failure 訊息。
 * 判斷依據：這次 update 只送了 1 個欄位、那個欄位是 `STATUS_ENABLED`
 * （`['啟用','停用']`）欄位池、新值也在那個池裡——三個條件同時成立才算，
 * 避免把「表單裡剛好只改了狀態欄位」的一般儲存誤判成狀態切換。
 */
import {
  applyResourceWriteSideEffects,
  EMPTY_TABLE_RESOURCES,
  enrichResourceRows,
  resourceDateRangeFilter,
  resourceQueryPassthrough,
  SEED_ROW_COUNTS,
  validateResourceWrite,
} from './resource-rules';

function toggleActionOf(resource: string, values: Row): LegacyActionKeyword {
  const keys = Object.keys(values);
  if (keys.length !== 1) return 'save';
  const [onlyKey] = keys;
  if (onlyKey === undefined) return 'save';
  const column = columnsFor(resource).find((c) => c.key === onlyKey);
  if (column === undefined || column.kind !== 'status') return 'save';
  if (JSON.stringify(column.pool) !== JSON.stringify(STATUS_ENABLED)) return 'save';
  const nextValue = values[onlyKey];
  if (nextValue === '啟用') return 'enable';
  if (nextValue === '停用') return 'disable';
  return 'save';
}

/**
 * crud-fix（B-P0 P38 反轉，2026-10-04）：create() 的通用欄位預設。
 *
 * B-P0 機器證據（docs/tools/parity-ops/95-FULLBEHAVIOR-W1-PROGRESS.md §12）：legacy
 * 新增後的列是「啟用＋可刪可停」（jobTitle.jsp/qb questionBank.jsp 的刪除條件
 * `erasable==1 && isEnable==1`），React 建立列卻沒有 status/erasable → 刪除鈕與
 * 啟停鈕都不出現（死列）、狀態顯示「停用」。在此補通用預設：
 *   ① 該頁啟停池（kind==='status' 且 pool 深等 STATUS_ENABLED）的狀態欄缺值或空
 *      → '啟用'（STATUS_ENABLED 首值）。STATUS_PROGRESS／STATUS_REVIEW 池的狀態欄
 *      （如 leave-leave-form 表單狀態）不補——語意不是啟停開關。
 *   ② `erasable` 缺值或空 → '1'。已帶值者（種子欄位／clone 來源列）一律尊重；
 *      leave 建立列的既有行為（可刪）不變。
 */
function createColumnDefaults(resource: string, values: Row): Row {
  const defaults: Row = {};
  const statusKey = columnsFor(resource).find(
    (column) => column.kind === 'status' && JSON.stringify(column.pool) === JSON.stringify(STATUS_ENABLED),
  )?.key;
  if (statusKey !== undefined && (values[statusKey] === undefined || values[statusKey] === '')) {
    defaults[statusKey] = STATUS_ENABLED[0];
  }
  if (values['erasable'] === undefined || values['erasable'] === '') {
    defaults['erasable'] = '1';
  }
  return defaults;
}

/**
 * crud-fix（P35 反轉，2026-10-04）：create() 對使用者帶值 id 的處理。
 *
 * id 欄解除唯讀的頁（config-job-title 的「職稱代碼」，jobTitle.jsp showModal
 * :12/:23 新增/修改皆可填、save() :126-131 code required）payload 會帶使用者填的
 * code——尊重它；空值（其他頁的 id 欄維持唯讀、openCreate 初始 ''）或撞既有列
 * （legacy 後端會擋重複 PK，mock 保守退回）→ 維持原 9000 起跳流水號 fallback。
 *
 * 白名單（CREATE_ID_FROM_VALUES）：只有行為層 modalIdEditable 的頁進來；
 * department 等頁 payload 的 id 是表單 code 欄值，凍結語意（row.id 一律 buildId，
 * 進度檔 §6.1）不變。
 */
const CREATE_ID_FROM_VALUES: ReadonlySet<string> = new Set(['config-job-title']);

function resolveCreatedId(values: Row, rows: readonly Row[], fallback: string): string {
  const requested = String(values['id'] ?? '').trim();
  if (requested === '') return fallback;
  if (rows.some((row) => String(row.id) === requested)) return fallback;
  return requested;
}

// ---------------------------------------------------------------------------
// 查詢
// ---------------------------------------------------------------------------

function matchesKeyword(row: Row, keyword: string): boolean {
  const needle = keyword.trim().toLowerCase();
  if (needle === '') {
    return true;
  }
  return Object.values(row).some((value) => String(value).toLowerCase().includes(needle));
}

function matchesFilters(row: Row, filters: Record<string, string>): boolean {
  return Object.entries(filters).every(
    ([key, value]) => value === '' || String(row[key]) === value,
  );
}

/**
 * 排序刻意用字串大小比較而不是 localeCompare：localeCompare 的中文排序結果依賴
 * 執行環境的 ICU 資料，同一份 fixture 在不同機器上可能排出不同順序，會直接破壞
 * 「同樣輸入得到同樣結果」這條最重要的規則。相同值時用原索引 tie-break，確保穩定。
 */
function applySort(rows: readonly Row[], sort: NonNullable<ListQuery['sort']>): Row[] {
  const direction = sort.direction === 'desc' ? -1 : 1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      const leftValue = String(left.row[sort.field] ?? '');
      const rightValue = String(right.row[sort.field] ?? '');
      if (leftValue < rightValue) {
        return -direction;
      }
      if (leftValue > rightValue) {
        return direction;
      }
      return left.index - right.index;
    })
    .map((entry) => entry.row);
}

function applyQuery(
  rows: readonly Row[],
  query: ListQuery,
  range?: (row: Row, filters: Record<string, string>) => boolean,
  passthrough = false,
): Row[] {
  // 批次 22（B2-2）：有日期範圍語意的 resource（課程行事曆群），beginDate/endDate 不走
  // 全等比對（row 無此鍵＝必落空），改交給 resource 專屬的日期範圍述詞（legacy：查詢
  // 表單的期間由 server 端過濾，getClassListAjax；mock 以 classBeginDateTimeString 鏡像）。
  // 批次 23（#25）：查詢過濾為 server 端語意的頁群（form/formTemplate 查詢頁）＝種子直出。
  const equalityFilters = passthrough
    ? {}
    : range === undefined
      ? (query.filters ?? {})
      : Object.fromEntries(
          Object.entries(query.filters ?? {}).filter(([key]) => key !== 'beginDate' && key !== 'endDate'),
        );
  let result = rows.filter(
    (row) =>
      matchesKeyword(row, query.keyword ?? '') &&
      matchesFilters(row, equalityFilters) &&
      (passthrough || range === undefined || range(row, query.filters ?? {})),
  );
  if (query.sort !== undefined) {
    result = applySort(result, query.sort);
  }
  return result;
}

// ---------------------------------------------------------------------------
// MockDataSource
// ---------------------------------------------------------------------------

export interface MockDataSourceOptions {
  /** 預設 'default'。決定筆數與錯誤／延遲行為。 */
  scenario?: ScenarioState;
  /** loading scenario 的延遲毫秒。預設 DEFAULT_LOADING_DELAY_MS。 */
  loadingDelayMs?: number;
}

/**
 * 記憶體版 DataSource，以 localStorage 為後盾（batch 0）。
 *
 * 確定性：資料由 fixtures.buildRows() 依 resource 產生，沒有 Math.random()／Date.now()，
 * 所以同樣的 (resource, query, scenario) 在**沒有 localStorage 既存資料**時一定得到
 * 同樣結果。
 *
 * CRUD：create／update／remove 改這個實例的 Map，同時 write-through 寫回 localStorage
 * （見 `persist()`）。重新載入頁面（＝新的實例）**不會**回到初始狀態——`rowsFor()` 第一次
 * 用到某 resource 時，會先看 localStorage 有沒有這個 (scenario, resource) 的既存資料，
 * 有就用它，沒有才用 fixtures 產生種子資料。這是刻意的行為變更（batch 0 之前是重新整理
 * 就清空，見 git 歷史）：雛形要能示範「使用者操作過的資料撐得過重新整理」，不再是
 * 每次重新整理都回到 demo 起始狀態。真的要回到起始狀態，走「重置測試資料」
 * （`clearAllPersistedMockData()` + `provider.tsx` 的 `useResetTestData()`），
 * 不是靠重新整理頁面。
 */
export class MockDataSource implements DataSource {
  readonly scenario: ScenarioState;
  private readonly loadingDelayMs: number;
  private readonly store = new Map<string, Row[]>();
  /** 每個 resource 新增過幾筆，用來產生不會撞號的新編號。 */
  private readonly createdCount = new Map<string, number>();

  constructor(options: MockDataSourceOptions = {}) {
    this.scenario = options.scenario ?? 'default';
    this.loadingDelayMs = options.loadingDelayMs ?? DEFAULT_LOADING_DELAY_MS;
  }

  async list(resource: string, query: ListQuery = {}): Promise<ListResult> {
    // FC-5 R3：匯出鈕走的是同一個 list()，但要能觸發它自己專屬的 legacy failure
    // 訊息（動作關鍵字 'export'），不能跟一般查詢共用同一條「查詢失敗」訊息。
    // FC-5 R4（C1-c‴ 修 bug）：kind 也要跟著分開——匯出以前一律標 'read'，導致
    // `write-error`（只擋 kind==='write'）永遠攔不到匯出，'export' 動作關鍵字的訊息
    // 結構上就打不到（R3 driveToAction() 明明假設「write-error 也會擋匯出」，
    // 但 kind 標的是 'read'，兩處對不起來——R4 觸發 13 條碰撞訊息時實測發現，見
    // report-R4.md）。標成獨立的 'export'（不是 'write'），'validation-error' 分支
    // 仍只認 'write'，匯出鈕點下去不會平白冒出欄位驗證錯誤。
    await this.gate(query.export === true ? 'export' : 'read', resource, query.export === true ? 'export' : 'query');
    const matched = applyQuery(
      this.rowsFor(resource),
      query,
      resourceDateRangeFilter(resource),
      resourceQueryPassthrough(resource),
    );
    const pageSize = query.pageSize ?? DEFAULT_PAGE_SIZE;
    const page = query.page ?? 1;
    const start = Math.max(0, (page - 1) * pageSize);
    return { rows: matched.slice(start, start + pageSize), total: matched.length };
  }

  async get(resource: string, id: string): Promise<Row | null> {
    await this.gate('read', resource, 'query');
    return this.rowsFor(resource).find((row) => String(row.id) === id) ?? null;
  }

  async create(resource: string, values: Row): Promise<Row> {
    await this.gate('write', resource, 'save');
    const rows = this.rowsFor(resource);
    // 批次 1（config）：業務規則層的寫入驗證（角色重複、年度範圍…）。
    const violation = validateResourceWrite(resource, values, 'create', rows);
    if (violation !== undefined) {
      throw this.validationOrError(violation);
    }
    const sequence = (this.createdCount.get(resource) ?? 0) + 1;
    this.createdCount.set(resource, sequence);
    // crud-fix（B-P0 P38 反轉）：新列補通用預設，讓建立列不是「死列」——
    //   ① 啟停池的狀態欄沒帶值（或空字串）→ 補「啟用」（legacy 各頁後端新資料預設
    //      isEnable=1；STATUS_PROGRESS／STATUS_REVIEW 池的狀態欄不補，leave 表單
    //      狀態語意不受影響）；
    //   ② `erasable` 沒帶（或空字串）→ 補 '1'（legacy 刪除/啟停連結條件
    //      `row.erasable==1 && row.isEnable==1`；已帶值者如 resource-rules 種子/
    //      clone 來源列一律尊重，leave 建立列的行為不變）。
    const defaults = createColumnDefaults(resource, values);
    // crud-fix（P35 反轉）：id 欄解除唯讀的頁（config-job-title 的 code 欄，
    // jobTitle.jsp showModal :12/:23 新增/修改皆可填）由使用者帶值——非空且不與
    // 既有列撞號就用它；空值或撞號（legacy 後端會擋重複，mock 保守退回流水號）
    // 維持原 9000 起跳流水號，不會跟 fixture 既有的 0001.. 撞號。
    // 白名單限縮：department 等其他頁 payload 的 id 是表單 code 欄值，凍結語意＝
    // create 的 row.id 一律由 buildId 產生（進度檔 §6.1；DepartmentEditModal 註解）。
    const id = CREATE_ID_FROM_VALUES.has(resource)
      ? resolveCreatedId(values, rows, buildId(resource, 8999 + sequence))
      : buildId(resource, 8999 + sequence);
    const created: Row = { ...values, ...defaults, id };
    // 新增的資料放最前面，雛形操作完馬上在第一頁看得到。
    rows.unshift(created);
    applyResourceWriteSideEffects(resource, String(created.id), created, rows);
    this.persist(resource);
    return created;
  }

  async update(resource: string, id: string, values: Row): Promise<Row> {
    // FC-5 R3：狀態切換鈕（CrudListPage 的「啟用/停用」）也是走這個既有的 update()，
    // 不另開一個 DataSource 方法——`data-effect="update:<resource>"` 才會是真話
    // （criteria C5-j：按鈕宣稱的動作必須是真的打到那個方法，不是另外開一支方法後
    // 讓 dataEffect 說謊）。用「這次 update 只改了一個 status 欄位、且新值是
    // STATUS_ENABLED 池的字」這個特徵，判斷這是狀態切換而不是一般儲存，藉此決定
    // gate() 要用哪個動作關鍵字挑 confirm/failure 訊息。
    await this.gate('write', resource, toggleActionOf(resource, values));
    const rows = this.rowsFor(resource);
    const index = rows.findIndex((row) => String(row.id) === id);
    const existing = rows[index];
    if (index < 0 || existing === undefined) {
      throw new MockDataError('not-found', MESSAGE_NOT_FOUND);
    }
    // 批次 1（config）：業務規則層的寫入驗證。狀態切換／設為預設這類「單欄部分更新」
    // 不帶完整表單，不重跑整表驗證（toggleActionOf() 回非 'save' 即是部分更新）。
    if (toggleActionOf(resource, values) === 'save') {
      const violation = validateResourceWrite(resource, values, 'update', rows, id);
      if (violation !== undefined) {
        throw this.validationOrError(violation);
      }
    }
    // id 不可被覆寫，否則列表會出現兩筆同號。
    const updated: Row = { ...existing, ...values, id: existing.id };
    rows[index] = updated;
    applyResourceWriteSideEffects(resource, String(id), updated, rows);
    this.persist(resource);
    return updated;
  }

  async remove(resource: string, id: string): Promise<void> {
    await this.gate('write', resource, 'delete');
    const rows = this.rowsFor(resource);
    const index = rows.findIndex((row) => String(row.id) === id);
    if (index < 0) {
      throw new MockDataError('not-found', MESSAGE_NOT_FOUND);
    }
    rows.splice(index, 1);
    this.persist(resource);
  }

  /** 批次 1（config）：把 resource-rules 的驗證結果轉成 MockDataError。 */
  private validationOrError(violation: {
    kind: 'error' | 'field';
    message: string;
    field?: string;
  }): MockDataError {
    if (violation.kind === 'field' && violation.field !== undefined) {
      return new MockDataError(
        'validation-error',
        MESSAGE_VALIDATION,
        { [violation.field]: violation.message },
      );
    }
    // 業務規則衝突（例：角色重複）沒有欄位可掛，走整頁 alert；message 用 legacy 原文。
    return new MockDataError('error', violation.message);
  }

  /**
   * 第一次用到某個 resource 才產生資料，之後沿用同一個陣列（CRUD 才會生效）。
   * 「第一次用到」同時是 localStorage 既存資料的讀取時機：有既存資料就沿用（重新整理
   * 頁面後的正常路徑），沒有才用 fixtures 產生種子資料**並立刻寫入** localStorage
   * （下一次重新整理才有東西可讀）。
   */
  private rowsFor(resource: string): Row[] {
    const cached = this.store.get(resource);
    if (cached !== undefined) {
      return cached;
    }
    const persisted = readPersistedResource(this.scenario, resource);
    if (persisted !== null) {
      this.store.set(resource, persisted.rows);
      this.createdCount.set(resource, persisted.createdCount);
      return persisted.rows;
    }
    // 批次 52（§7.2/§8-B；§7.3）：逐 resource 種子列數——29 頁空表化（凍結真空表）優先；
    // SEED_ROW_COUNTS（config-role 9／config-job-title 12）次之；其餘照 scenario 全域值。
    const seedCount = EMPTY_TABLE_RESOURCES.has(resource)
      ? 0
      : (SEED_ROW_COUNTS[resource] ?? ROW_COUNTS[this.scenario]);
    const rows = enrichResourceRows(resource, buildRows(resource, seedCount));
    this.store.set(resource, rows);
    this.persist(resource);
    return rows;
  }

  /** write-through：把這個 resource 目前的記憶體狀態同步寫回 localStorage。 */
  private persist(resource: string): void {
    writePersistedResource(this.scenario, resource, {
      rows: this.store.get(resource) ?? [],
      createdCount: this.createdCount.get(resource) ?? 0,
    });
  }

  /**
   * scenario 對「所有方法」的共同前置行為。
   * FC-5 R3：新增 `action` 參數——同一個 resource 的 confirm／failure 訊息可能同時有
   * 好幾條（刪除失敗／停用失敗／啟用失敗／儲存失敗…），不能再像 R2 那樣只挑一條代表；
   * 呼叫端（list/get/create/update/remove/toggleStatus）各自帶自己的動作關鍵字，
   * `resolveLegacyMessageForAction()` 依關鍵字選出對應那一條。
   */
  private async gate(
    kind: 'read' | 'write' | 'export',
    resource: string,
    action: LegacyActionKeyword,
  ): Promise<void> {
    switch (this.scenario) {
      case 'error':
        throw new MockDataError(
          'error',
          resolveLegacyMessageForAction(resource, 'failure', action)?.text ?? MESSAGE_ERROR,
        );
      case 'write-error':
        // FC-5 R3：只擋寫入——讀取正常才能先看到列表/表單，再按下某個具體動作看它失敗，
        // 讓「刪除失敗」「儲存失敗」「啟用/停用失敗」「匯出失敗」這些寫入專屬的訊息
        // 真的有機會被按到（'error' 連讀取都擋，頁面連載入都失敗，永遠按不到任何按鈕）。
        // FC-5 R4：'export' 跟 'write' 一樣要擋——匯出雖然語意上是唯讀操作，但跟
        // 刪除／儲存一樣是「使用者按下某個具體動作才會失敗」的那一類，不該跟著一般
        // 查詢被 'error' 整頁擋住（見上面 list() 的說明）。
        if (kind === 'write' || kind === 'export') {
          throw new MockDataError(
            'error',
            resolveLegacyMessageForAction(resource, 'failure', action)?.text ?? MESSAGE_ERROR,
          );
        }
        break;
      case 'unauthorized':
        throw new MockDataError('unauthorized', MESSAGE_UNAUTHORIZED);
      case 'validation-error':
        // 讀取正常，只有送出（create／update）才會退回欄位錯誤——這才是原系統的行為。
        if (kind === 'write') {
          // FC-5 R3（C1-c″）：這個 resource 若有多條 legacy validation 訊息，**全部**
          // 分配到欄位上同時顯示（不再只挑一條代表）——每個欄位各自的必填/格式錯誤本來就
          // 該各自顯示各自的訊息，這是真的逐欄位驗證，不是為了湊分子硬塞。
          // 訊息數超過可用欄位數時，多出來的進 `extraMessages`（呼叫端渲染成一份清單）。
          const allValidation = resolveAllLegacyMessages(resource, 'validation');
          // `id` 欄位是系統自產、不是使用者填的欄位（`kind === 'id'`），驗證錯誤不該掛在
          // 它身上——只挑非 id 的欄位才會落在真的會顯示 error 訊息的欄位上。
          const editableFields = columnsFor(resource).filter((f) => f.kind !== 'id');
          const fields: Record<string, string> = {};
          const extraMessages: string[] = [];
          if (allValidation.length === 0) {
            // 這個 resource 沒有 legacy validation 訊息——退回雛形通用的兩欄位錯誤文案
            // （維持既有測試與行為不變）。
            const firstKey = editableFields[0]?.key ?? 'title';
            const secondKey = editableFields[1]?.key ?? 'unit';
            fields[firstKey] = '請輸入名稱';
            if (secondKey !== firstKey) fields[secondKey] = '請選擇單位';
          } else {
            allValidation.forEach((item, i) => {
              const field = editableFields[i];
              if (field !== undefined) fields[field.key] = item.text;
              else extraMessages.push(item.text);
            });
          }
          throw new MockDataError('validation-error', MESSAGE_VALIDATION, fields, extraMessages);
        }
        break;
      case 'loading':
        await delay(this.loadingDelayMs);
        break;
      default:
        break;
    }
  }
}
